import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ReversementDu, Tableau2Bord } from '@troc/shared'
import { eur } from '@troc/shared'
import { api } from '../api'
import { Ecran } from '../composants/Ecran'
import { PastilleReversement } from '../composants/Pastille'
import { PorteGerant } from '../composants/PorteGerant'
import { Statistiques } from './Statistiques'
import { Documents } from './Documents'

const AUJOURD_HUI = new Date().toISOString().slice(0, 10)

export function Bilan() {
  return <PorteGerant>{(entetes) => <TableauDeBord entetes={entetes} />}</PorteGerant>
}

type Onglet = 'bord' | 'stats' | 'documents'

function TableauDeBord({ entetes }: { entetes: Record<string, string> }) {
  const [onglet, setOnglet] = useState<Onglet>('bord')
  const [tableau, setTableau] = useState<Tableau2Bord | null>(null)
  const [reversements, setReversements] = useState<ReversementDu[]>([])

  useEffect(() => {
    api<{ tableau: Tableau2Bord }>('/stats', { entetes })
      .then((r) => setTableau(r.tableau))
      .catch(() => undefined)
    api<{ deposants: ReversementDu[] }>('/reversements', { entetes })
      .then((r) => setReversements(r.deposants))
      .catch(() => undefined)
  }, [entetes])

  if (!tableau) {
    return (
      <Ecran titre="Tableau de bord" sousTitre="Chargement…">
        <div className="vide">Calcul en cours…</div>
      </Ecran>
    )
  }

  const evolution =
    tableau.caMoisPrecedentCentimes > 0
      ? Math.round(
          ((tableau.caMoisCentimes - tableau.caMoisPrecedentCentimes) /
            tableau.caMoisPrecedentCentimes) *
            100,
        )
      : null

  const maxSerie = Math.max(1, ...tableau.serie.map((point) => point.centimes))
  const maxCategorie = Math.max(1, ...tableau.parCategorie.map((c) => c.centimes))

  const onglets = (
    <div className="seg" style={{ marginLeft: 'auto' }}>
      <button
        type="button"
        className="seg-opt"
        aria-pressed={onglet === 'bord'}
        onClick={() => setOnglet('bord')}
      >
        Tableau de bord
      </button>
      <button
        type="button"
        className="seg-opt"
        aria-pressed={onglet === 'stats'}
        onClick={() => setOnglet('stats')}
      >
        Statistiques
      </button>
      <button
        type="button"
        className="seg-opt"
        aria-pressed={onglet === 'documents'}
        onClick={() => setOnglet('documents')}
      >
        Documents
      </button>
    </div>
  )

  if (onglet === 'documents') {
    return (
      <Ecran titre="Bilan" sousTitre="Registre des documents émis" outils={onglets}>
        <Documents entetes={entetes} />
      </Ecran>
    )
  }

  if (onglet === 'stats') {
    return (
      <Ecran titre="Bilan" sousTitre="Statistiques de la boutique" outils={onglets}>
        <Statistiques entetes={entetes} />
      </Ecran>
    )
  }

  return (
    <Ecran titre="Tableau de bord" sousTitre="Boutique · mois en cours" outils={onglets}>
      <div className="zone-defilante" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div className="bandeau-kpi">
          <div className="kpi">
            <div className="etiquette">CA du mois</div>
            <div className="valeur">{eur(tableau.caMoisCentimes)}</div>
            <div className="detail" style={{ color: 'var(--color-accent)' }}>
              {evolution === null
                ? 'premier mois de données'
                : `${evolution >= 0 ? '+' : ''}${evolution} % vs mois précédent`}
            </div>
          </div>
          <div className="kpi">
            <div className="etiquette">Tickets</div>
            <div className="valeur">{tableau.nbTickets}</div>
            <div className="detail" style={{ color: 'var(--color-accent)' }}>
              {tableau.nbTicketsAujourdhui} aujourd’hui
            </div>
          </div>
          <div className="kpi">
            <div className="etiquette">Panier moyen</div>
            <div className="valeur">{eur(tableau.panierMoyenCentimes)}</div>
            <div className="detail">{tableau.articlesEnDepot} articles en rayon</div>
          </div>
          <div className="kpi">
            <div className="etiquette">Rotation</div>
            <div className="valeur">{Math.round(tableau.rotation * 100)} %</div>
            <div className="detail">vendu sous 30 jours</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4>Chiffre d’affaires — 14 derniers jours</h4>
            <div className="histogramme">
              {tableau.serie.map((point) => (
                <div
                  key={point.jour}
                  className={point.jour === AUJOURD_HUI ? 'colonne aujourdhui' : 'colonne'}
                  title={`${point.jour} — ${eur(point.centimes)}`}
                >
                  <div className="valeur">
                    {point.centimes >= 100_000
                      ? `${(point.centimes / 100_000).toFixed(1)}k`
                      : Math.round(point.centimes / 100) || ''}
                  </div>
                  <div
                    className="barre"
                    style={{ height: `${Math.round((point.centimes / maxSerie) * 100)}%` }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              {tableau.serie.map((point) => (
                <div
                  key={point.jour}
                  className="sourdine"
                  style={{ flex: 1, fontSize: 10, textAlign: 'center' }}
                >
                  {point.libelle}
                </div>
              ))}
            </div>
          </div>

          <div style={{ width: 340, flex: 'none', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <h4>Par catégorie — ce mois</h4>
              {tableau.parCategorie.map((categorie) => (
                <div
                  key={categorie.categorie}
                  style={{ padding: '8px 0', borderBottom: '1px solid var(--color-hairline)' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>{categorie.categorie}</span>
                    <span className="titre">{eur(categorie.centimes)}</span>
                  </div>
                  <div className="jauge">
                    <div
                      style={{ width: `${Math.round((categorie.centimes / maxCategorie) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
              {tableau.parCategorie.length === 0 && (
                <div className="sourdine" style={{ fontSize: 12 }}>
                  Aucune vente ce mois-ci.
                </div>
              )}
            </div>

            <div className="encart-jaune">
              <div className="etiquette">
                {tableau.reversements.payable ? 'À reverser dès maintenant' : 'À traiter avant le 15'}
              </div>
              <div className="chiffre" style={{ fontSize: 34, lineHeight: 1.1 }}>
                {eur(tableau.reversements.totalCentimes)}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                dus à {tableau.reversements.nbDeposants} déposant
                {tableau.reversements.nbDeposants > 1 ? 's' : ''}
                {tableau.reversements.nbVirements > 0 &&
                  ` — dont ${tableau.reversements.nbVirements} par virement (> 3 000 €)`}
                .
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4>Reversements en attente</h4>
          <table className="tableau">
            <thead>
              <tr>
                <th>N°</th>
                <th>Déposant</th>
                <th className="a-droite">Ventes</th>
                <th className="a-droite">Payable</th>
                <th className="a-droite">En attente</th>
                <th>Mode</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reversements.map((reversement) => (
                <tr key={reversement.deposantId}>
                  <td className="ref">{reversement.deposantRef}</td>
                  <td className="titre">{reversement.deposantNom}</td>
                  <td className="a-droite">{reversement.nbVentes}</td>
                  <td className="a-droite chiffre">{eur(reversement.montantCentimes)}</td>
                  <td className="a-droite sourdine">
                    {reversement.attenteCentimes > 0 ? eur(reversement.attenteCentimes) : '—'}
                  </td>
                  <td>
                    <PastilleReversement mode={reversement.mode} />
                  </td>
                  <td className="a-droite">
                    <Link className="btn btn-fantome" to={`/deposants/${reversement.deposantId}`}>
                      Ouvrir la fiche
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {reversements.length === 0 && <div className="vide">Aucun reversement en attente.</div>}
        </div>
      </div>
    </Ecran>
  )
}
